import React, { useEffect, useMemo, useState, useRef } from "react";
import { FormService, FormField } from "@shared/services/FormServices";
import { SharedFormArray } from "@shared/components/SharedFormArray";
import { SegmentedControl } from "@shared/components/SegmentedControl";
import { useParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { toast } from "@shared/hooks/use-toast";
import {
  useUtilityServices,
  useGetPlanDetail,
  useUpdatePlan,
  useGetPlanDetailByTab,
  useIncludedIn,
} from "../../../hooks";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { useQueries } from "@tanstack/react-query";
import { PlanApi } from "../../../api";

interface ChargesTaxesProps {
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  allStepData?: any;
}

const getSelectedServices = (val: any): string[] => {
  if (Array.isArray(val)) {
    return val
      .map((s: any) => (typeof s === "object" && s !== null ? (s.value ?? s.label ?? String(s)) : String(s)))
      .flatMap((s: string) => s.split(","))
      .map((s: string) => s.trim())
      .filter(Boolean);
  } else if (typeof val === "string") {
    return val.split(",").map((s) => s.trim()).filter(Boolean);
  } else if (val !== null && val !== undefined && val !== "") {
    return [String(val).trim()];
  }
  return [];
};

const getSelectedValues = (val: any): string[] => {
  if (Array.isArray(val)) {
    return val
      .map((s: any) => (typeof s === "object" && s !== null ? (s.value ?? s.label ?? s.key ?? String(s)) : String(s)))
      .flatMap((s: string) => s.split(","))
      .map((s: string) => s.trim())
      .filter(Boolean);
  } else if (typeof val === "string") {
    return val.split(",").map((s) => s.trim()).filter(Boolean);
  } else if (val !== null && val !== undefined && val !== "") {
    return [String(val).trim()];
  }
  return [];
};

const findDuplicateName = (items: any[], getName: (item: any) => string) => {
  const seen = new Set<string>();
  for (const item of items || []) {
    const rawName = getName(item);
    if (!rawName) continue;
    const normalized = String(rawName).trim().toLowerCase();
    if (!normalized) continue;
    if (seen.has(normalized)) {
      return String(rawName).trim();
    }
    seen.add(normalized);
  }
  return null;
};

const normalizeChargeType = (
  rawType: any,
  amount?: any,
  rate?: any,
): "percentage" | "flatRate" => {
  const str = String(rawType || "").toLowerCase().trim();
  if (
    str.includes("flat") ||
    str.includes("amount") ||
    str === "fixed"
  ) {
    return "flatRate";
  }
  if (
    str.includes("percent") ||
    str.includes("%")
  ) {
    return "percentage";
  }
  if (
    amount != null &&
    amount !== "" &&
    Number(amount) !== 0 &&
    (rate == null || rate === "" || Number(rate) === 0)
  ) {
    return "flatRate";
  }
  return "percentage";
};

export const ChargesTaxes: React.FC<ChargesTaxesProps> = ({
  data,
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  allStepData,
}) => {
  console.log("data iss", data);
  const { action, planId } = useParams();
  const [hasInitialData] = useState(() => Object.keys(data || {}).length > 0);
  const queryPlanId = hasInitialData ? undefined : planId;
  const remoteUtilityId = useRemoteUtilityId();
  const { data: planDetail } = useGetPlanDetailByTab(queryPlanId, "charges");
  const { mutateAsync: updatePlan, isPending } = useUpdatePlan();
  const { data: servicesResult } = useUtilityServices();
  const currency = useCurrency();
  const serviceOptions = useMemo(() => {
    const responseData = servicesResult?.result || servicesResult;
    if (!Array.isArray(responseData)) return [];
    return responseData
      .filter((service: any) => service.isActive === true)
      .map((service: any) => ({
        label: service.name,
        value: service.name,
      }));
  }, [servicesResult]);
  const { data: includedInData } = useIncludedIn();
  const includedInOptions = useMemo(
    () =>
      includedInData?.result?.map((item) => ({
        value: item.key,
        label: item.value,
      })) || [],
    [includedInData],
  );

  const unmeteredServices = useMemo(() => {
    const responseData = servicesResult?.result || servicesResult;
    if (!Array.isArray(responseData)) return [];
    return responseData
      .filter((service: any) => service.isMeterRequired === false)
      .map((service: any) => service.name);
  }, [servicesResult]);
  const [taxMode, setTaxMode] = useState("common");
  const [fixedChargeMode, setFixedChargeMode] = useState("common");
  const [surchargeMode, setSurchargeMode] = useState("common");
  const chargesForm = FormService.createForm({
    fields: [
      { name: "linkedCharges", type: "form-array" },
      { name: "fixedCharges", type: "form-array" },
      { name: "surcharges", type: "form-array" },
      { name: "taxes", type: "form-array" },
    ] as any[],
    defaultValues: data || {
      linkedCharges: allStepData?.[2]?.unmeteredLinkedCharges || [],
      chargeDeterminants: [],
      taxes: [],
      fixedCharges: [],
      surcharges: [],
    },
    mode: "onChange",
  });

  const taxesCache = React.useRef<{ common: any[]; "parameter-wise": any[] }>({
    common: [],
    "parameter-wise": [],
  });
  const fixedChargesCache = React.useRef<{
    common: any[];
    "parameter-wise": any[];
  }>({ common: [], "parameter-wise": [] });
  const surchargesCache = React.useRef<{
    common: any[];
    "parameter-wise": any[];
  }>({ common: [], "parameter-wise": [] });

  const handleTaxModeChange = (mode: string) => {
    taxesCache.current[taxMode as "common" | "parameter-wise"] =
      chargesForm.getValues("taxes") || [];
    setTaxMode(mode);
    chargesForm.setValue(
      "taxes",
      taxesCache.current[mode as "common" | "parameter-wise"] || [],
    );
  };

  const handleFixedChargeModeChange = (mode: string) => {
    fixedChargesCache.current[fixedChargeMode as "common" | "parameter-wise"] =
      chargesForm.getValues("fixedCharges") || [];
    setFixedChargeMode(mode);
    chargesForm.setValue(
      "fixedCharges",
      fixedChargesCache.current[mode as "common" | "parameter-wise"] || [],
    );
  };

  const handleSurchargeModeChange = (mode: string) => {
    surchargesCache.current[surchargeMode as "common" | "parameter-wise"] =
      chargesForm.getValues("surcharges") || [];
    setSurchargeMode(mode);
    chargesForm.setValue(
      "surcharges",
      surchargesCache.current[mode as "common" | "parameter-wise"] || [],
    );
  };

  const formDataWatch = chargesForm.watch();
  const formTaxes = formDataWatch?.taxes || [];
  const formFixedCharges = formDataWatch?.fixedCharges || [];
  const formSurcharges = formDataWatch?.surcharges || [];
  const formChargeDeterminants = formDataWatch?.chargeDeterminants || [];

  const prevServicesRef = React.useRef<string[]>([]);
  const selectedUtilityServices = useMemo(() => {
    const allServices = new Set<string>();
    const extract = (items: any[]) => {
      if (!Array.isArray(items)) return;
      items.forEach((item) => {
        const services = getSelectedServices(item?.utilityService);
        services.forEach((s) => allServices.add(s));
      });
    };
    extract(formTaxes);
    extract(formFixedCharges);
    extract(formSurcharges);
    extract(formChargeDeterminants);

    const newServices = Array.from(allServices).sort();
    if (
      prevServicesRef.current.length !== newServices.length ||
      !prevServicesRef.current.every((v, i) => v === newServices[i])
    ) {
      prevServicesRef.current = newServices;
    }
    return prevServicesRef.current;
  }, [formTaxes, formFixedCharges, formSurcharges, formChargeDeterminants]);

  // Make individual API calls per unique service so we can map service → parameters
  const parameterQueries = useQueries({
    queries: selectedUtilityServices.map((service) => ({
      queryKey: ["planParameter", remoteUtilityId, service],
      queryFn: () => PlanApi.getParameter(remoteUtilityId, service),
      enabled: !!remoteUtilityId && !!service,
      staleTime: Infinity,
    })),
  });

  // Build service → parameters[] map
  const parameterQueryData = parameterQueries.map((q) => q.data);
  console.log("parameterQueries JSON:", JSON.stringify(parameterQueryData));
  const parametersByService = useMemo(() => {
    const map = new Map<string, any[]>();
    selectedUtilityServices.forEach((service, idx) => {
      const data = parameterQueryData[idx];
      const list = data?.result || data;
      map.set(service, Array.isArray(list) ? list : []);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUtilityServices, JSON.stringify(parameterQueryData)]);

  // Flat list of all parameters (for edit-mode population / lookups)
  const allParametersList = useMemo(() => {
    const seen = new Set<string>();
    const result: any[] = [];
    parametersByService.forEach((params) => {
      params.forEach((p) => {
        const id = String(p.id);
        if (!seen.has(id)) {
          seen.add(id);
          result.push(p);
        }
      });
    });
    return result;
  }, [parametersByService]);

  console.log("all parameteres aree", allParametersList);

  const parametersOptions = useMemo(() => {
    return allParametersList.map((p: any) => ({
      label: p.name,
      value: String(p.id),
      ...(p.allowedValues &&
        p.allowedValues.length > 0 && {
          options: p.allowedValues.map((v: any) => ({ label: v, value: v })),
        }),
    }));
  }, [allParametersList]);

  // Per-row getOptions: filter parameters by THIS row's selected utility services
  const getParameterOptionsForRow = React.useCallback(
    (rowData: any) => {
      const rowServices = getSelectedServices(rowData?.utilityService);
      if (rowServices.length === 0) return [];
      const seen = new Set<string>();
      const result: { label: string; value: string }[] = [];
      rowServices.forEach((service) => {
        const params = parametersByService.get(service) || [];
        params.forEach((p: any) => {
          const id = String(p.id);
          if (!seen.has(id)) {
            seen.add(id);
            result.push({ label: p.name, value: id });
          }
        });
      });
      return result;
    },
    [parametersByService],
  );

  // Per-row getOptions: get allowed values for THIS row's selected parameter
  const getValueOptionsForRow = React.useCallback(
    (rowData: any) => {
      const selectedParamId = rowData?.parameter;
      if (!selectedParamId) return [];
      const sParamId = String(selectedParamId).toLowerCase();
      const param = allParametersList.find((p: any) => {
        return (
          String(p.id ?? "").toLowerCase() === sParamId ||
          String(p.parameter_id ?? "").toLowerCase() === sParamId ||
          String(p.name ?? "").toLowerCase() === sParamId ||
          String(p.code ?? "").toLowerCase() === sParamId ||
          String(p.key ?? "").toLowerCase() === sParamId
        );
      });
      if (!param) return [];
      const allowedValues =
        param?.allowedValues ||
        param?.allowed_values ||
        param?.allowedValuesOptions ||
        param?.values ||
        [];
      if (Array.isArray(allowedValues) && allowedValues.length > 0) {
        return allowedValues.map((val: any) => {
          if (typeof val === "object" && val !== null) {
            const label =
              val?.display ||
              val?.label ||
              val?.name ||
              val?.key ||
              String(val?.value || "");
            const value = String(val?.key ?? val?.value ?? val?.id ?? label);
            return { label, value };
          }
          return { label: String(val), value: String(val) };
        });
      }
      return [];
    },
    [allParametersList],
  );

  const linkedChargesFields: FormField[] = useMemo(
    () => [
      {
        name: "name",
        label: "Name",
        type: "text",
        placeholder: "New Linked Charge",
        disabled: (v) =>
          unmeteredServices.includes(v?.name) || !!v?.isUnmetered,
      },
      {
        name: "linkedService",
        label: "Linked Service",
        type: "select",
        options: serviceOptions,
      },
      { name: "usage", label: "Usage (%)", type: "number", suffix: "%" },
      {
        name: "chargeUnit",
        label: `Rate (${currency}/UNIT)`,
        type: "text",
        suffix: "/ KL",
        disabled: (v) =>
          unmeteredServices.includes(v?.name) || !!v?.isUnmetered,
      },
    ],
    [serviceOptions, unmeteredServices, currency],
  );

  const commonTaxFields: FormField[] = useMemo(
    () => [
      {
        name: "taxName",
        label: "Tax Name",
        type: "text",
        placeholder: "e.g. State Utility Tax",
      },
      {
        name: "type",
        label: "Type",
        type: "select",
        options: [
          { label: "Percentage %", value: "percentage" },
          { label: `Flat ${currency}`, value: "flatRate" },
        ],
      },
      {
        name: "rate",
        label: "Rate (%)",
        type: "number",
        suffix: "%",
        showWhen: (v) => v?.type !== "flatRate",
      },
      {
        name: "amount",
        label: `Amount (${currency})`,
        type: "number",
        showWhen: (v) => v?.type === "flatRate",
      },
    ],
    [currency],
  );

  const parameterWiseTaxFields: FormField[] = useMemo(
    () => [
      {
        name: "utilityService",
        label: "Utility Service",
        type: "multi-select",
        options: serviceOptions,
        className: "md:col-span-12 lg:col-span-4",
      },
      {
        name: "parameter",
        label: "Parameter",
        type: "select",
        getOptions: getParameterOptionsForRow,
        className: "md:col-span-6 lg:col-span-4",
      },
      {
        name: "value",
        label: "Value",
        type: "multi-select",
        getOptions: getValueOptionsForRow,
        defaultValue: [],
        className: "md:col-span-6 lg:col-span-4",
      },
      {
        name: "taxName",
        label: "Tax Name",
        type: "text",
        placeholder: "e.g. State Utility Tax",
        className: "md:col-span-4 lg:col-span-4",
      },
      {
        name: "type",
        label: "Type",
        type: "select",
        options: [
          { label: "Percentage %", value: "percentage" },
          { label: `Flat ${currency}`, value: "flatRate" },
        ],
        className: "md:col-span-4 lg:col-span-4",
      },
      {
        name: "rate",
        label: "Rate (%)",
        type: "number",
        suffix: "%",
        className: "md:col-span-4 lg:col-span-4",
        showWhen: (v) => v?.type !== "flatRate",
      },
      {
        name: "amount",
        label: `Amount (${currency})`,
        type: "number",
        className: "md:col-span-4 lg:col-span-4",
        showWhen: (v) => v?.type === "flatRate",
      },
    ],
    [
      serviceOptions,
      getParameterOptionsForRow,
      getValueOptionsForRow,
      currency,
    ],
  );

  const commonFixedChargeFields: FormField[] = useMemo(
    () => [
      {
        name: "name",
        label: "Name",
        type: "text",
        placeholder: "Plan Service Fee",
      },
      { name: "amount", label: `Amount (${currency})`, type: "number" },
      {
        name: "includeIn",
        label: "Include In",
        type: "select",
        options: includedInOptions,
      },
    ],
    [includedInOptions],
  );

  const parameterWiseFixedChargeFields: FormField[] = useMemo(
    () => [
      {
        name: "utilityService",
        label: "Utility Service",
        type: "multi-select",
        options: serviceOptions,
        defaultValue: [],
        className: "md:col-span-4 lg:col-span-4",
      },
      {
        name: "parameter",
        label: "Parameter",
        type: "select",
        getOptions: getParameterOptionsForRow,
        className: "md:col-span-4 lg:col-span-4",
      },
      {
        name: "value",
        label: "Value",
        type: "multi-select",
        getOptions: getValueOptionsForRow,
        defaultValue: [],
        className: "md:col-span-4 lg:col-span-4",
      },
      {
        name: "chargeName",
        label: "Charge Name",
        type: "text",
        placeholder: "New Charge",
        className: "md:col-span-4 lg:col-span-4",
      },
      {
        name: "amount",
        label: `Amount (${currency})`,
        type: "number",
        className: "md:col-span-4 lg:col-span-4",
      },
      {
        name: "includeIn",
        label: "Include In",
        type: "select",
        options: includedInOptions,
        className: "md:col-span-4 lg:col-span-4",
      },
    ],
    [
      serviceOptions,
      getParameterOptionsForRow,
      getValueOptionsForRow,
      includedInOptions,
      currency,
    ],
  );

  const commonSurchargeFields: FormField[] = useMemo(
    () => [
      { name: "name", label: "Name", type: "text", placeholder: "New charge" },
      {
        name: "type",
        label: "Type",
        type: "select",
        options: [
          { label: "Percentage %", value: "percentage" },
          { label: `Flat ${currency}`, value: "flatRate" },
        ],
      },
      {
        name: "rate",
        label: "Rate (%)",
        type: "number",
        suffix: "%",
        showWhen: (v) => v?.type !== "flatRate",
      },
      {
        name: "amount",
        label: `Amount (${currency})`,
        type: "number",
        showWhen: (v) => v?.type === "flatRate",
      },
    ],
    [currency],
  );

  const parameterWiseSurchargeFields: FormField[] = useMemo(
    () => [
      {
        name: "utilityService",
        label: "Utility Service",
        type: "multi-select",
        options: serviceOptions,
        defaultValue: [],
        className: "md:col-span-4 lg:col-span-4",
      },
      {
        name: "parameter",
        label: "Parameter",
        type: "select",
        getOptions: getParameterOptionsForRow,
        className: "md:col-span-4 lg:col-span-4",
      },
      {
        name: "value",
        label: "Value",
        type: "multi-select",
        getOptions: getValueOptionsForRow,
        defaultValue: [],
        searchable: true,
        className: "md:col-span-4 lg:col-span-4",
      },
      {
        name: "chargeName",
        label: "Charge Name",
        type: "text",
        placeholder: "New Charge",
        className: "md:col-span-4 lg:col-span-4",
      },
      {
        name: "type",
        label: "Type",
        type: "select",
        options: [
          { label: "Percentage %", value: "percentage" },
          { label: `Flat ${currency}`, value: "flatRate" },
        ],
        className: "md:col-span-4 lg:col-span-4",
      },
      {
        name: "rate",
        label: "Rate (%)",
        type: "number",
        suffix: "%",
        className: "md:col-span-4 lg:col-span-4",
        showWhen: (v) => v?.type !== "flatRate",
      },
      {
        name: "amount",
        label: `Amount (${currency})`,
        type: "number",
        className: "md:col-span-4 lg:col-span-4",
        showWhen: (v) => v?.type === "flatRate",
      },
    ],
    [
      serviceOptions,
      getParameterOptionsForRow,
      getValueOptionsForRow,
      currency,
    ],
  );
  const isPopulated = React.useRef(false);
  const isLocalStorageRestored = React.useRef(false);

  useEffect(() => {
    if (hasInitialData && data && !isLocalStorageRestored.current) {
      if (data.taxMode) setTaxMode(data.taxMode);
      if (data.fixedChargeMode) setFixedChargeMode(data.fixedChargeMode);
      if (data.surchargeMode) setSurchargeMode(data.surchargeMode);
      const normalizeArr = (arr: any[]) =>
        Array.isArray(arr)
          ? arr.map((item: any) => {
              const normType = item.type
                ? normalizeChargeType(item.type, item.amount, item.rate)
                : undefined;
              const rateVal = item.rate != null && item.rate !== "" ? item.rate : item.amount;
              const amountVal = item.amount != null && item.amount !== "" ? item.amount : item.rate;
              return {
                ...item,
                ...(normType ? { type: normType } : {}),
                ...(rateVal !== undefined ? { rate: rateVal } : {}),
                ...(amountVal !== undefined ? { amount: amountVal } : {}),
                utilityService: getSelectedServices(
                  item.utilityService || item.utilityServices || item.utility_services,
                ),
                value: getSelectedValues(
                  item.value || item.parameterValues || item.parameter_values,
                ),
              };
            })
          : [];
      const normalizedData = {
        ...data,
        fixedCharges: normalizeArr(data.fixedCharges),
        surcharges: normalizeArr(data.surcharges),
        taxes: normalizeArr(data.taxes),
      };
      FormService.populateForm(chargesForm, normalizedData, [
        { name: "linkedCharges", type: "form-array" },
        { name: "fixedCharges", type: "form-array" },
        { name: "surcharges", type: "form-array" },
        { name: "taxes", type: "form-array" },
      ] as any[]);
      isLocalStorageRestored.current = true;
    }
  }, [hasInitialData, data, chargesForm]);

  useEffect(() => {
    isPopulated.current = false;
  }, [planId]);

  useEffect(() => {
    if ((!planId || planId === "create") && !isPopulated.current) {
      const step2Unmetered = allStepData?.[2]?.unmeteredLinkedCharges || [];
      const initialLinkedCharges =
        data?.linkedCharges && data.linkedCharges.length > 0
          ? data.linkedCharges
          : step2Unmetered.map((u: any) => ({
              name: u.name,
              linkedService: u.linkedService,
              chargeUnit: u.chargeUnit || u.rate || "",
              usage: u.usage || 0,
              rateAmount: u.rateAmount || 0,
            }));

      const mappedData = {
        linkedCharges: initialLinkedCharges,
        chargeDeterminants: data?.chargeDeterminants || [],
        taxes: data?.taxes || [],
        fixedCharges: data?.fixedCharges || [],
        surcharges: data?.surcharges || [],
      };
      FormService.populateForm(chargesForm, mappedData, [
        { name: "linkedCharges", type: "form-array" },
        { name: "fixedCharges", type: "form-array" },
        { name: "surcharges", type: "form-array" },
        { name: "taxes", type: "form-array" },
      ] as any[]);
      isPopulated.current = true;
    }
  }, [planId, chargesForm, allStepData, data]);

  useEffect(() => {
    if (hasInitialData) return;
    if (
      planDetail?.result &&
      planId &&
      planId !== "create" &&
      !isPopulated.current
    ) {
      const res = planDetail.result;

      const getParameterName = (id: any) => {
        const param = allParametersList.find(
          (p: any) => String(p.id) === String(id),
        );
        return param?.name || "";
      };

      let dbLinkedCharges = (res.linkedCharges || res.linked_charges || []).map(
        (lc: any) => ({
          name: lc.name,
          linkedService: lc.linkedService || lc.linked_service,
          chargeUnit: lc.chargeRate || lc.charge_rate,
          usage: lc.usage || "",
        }),
      );

      const step2Unmetered = allStepData?.[2]?.unmeteredLinkedCharges || [];
      console.warn("ChargesTaxes - res:", res);
      console.warn("ChargesTaxes - step2Unmetered:", step2Unmetered);
      console.warn("ChargesTaxes - initial dbLinkedCharges:", dbLinkedCharges);

      step2Unmetered.forEach((u: any) => {
        const existing = dbLinkedCharges.find(
          (dbLc: any) => dbLc.name === u.name,
        );
        if (existing) {
          existing.chargeUnit = u.chargeUnit;
          existing.rateAmount = u.rateAmount;
        } else {
          dbLinkedCharges.push({
            name: u.name,
            linkedService: u.linkedService,
            chargeUnit: u.chargeUnit || u.rate || "",
            usage: u.usage || 0,
            rateAmount: u.rateAmount || 0,
          });
        }
      });
      console.warn(
        "ChargesTaxes - final dbLinkedCharges after merge:",
        dbLinkedCharges,
      );

      const mappedData = {
        linkedCharges: dbLinkedCharges,
        fixedCharges: (res.fixedCharges || res.fixed_charges || []).map(
          (fc: any) => ({
            chargeName: fc.name,
            name: fc.name,
            amount: fc.amount,
            includeIn: fc.includeIn || fc.include_in,
            utilityService: getSelectedServices(
              fc.utilityServices || fc.utility_services || fc.utilityService,
            ),
            parameter: String(fc.parameterId || fc.parameter_id || fc.parameter || ""),
            value: getSelectedValues(
              fc.parameterValues || fc.parameter_values || fc.value,
            ),
          }),
        ),
        surcharges: (res.surcharges || []).map((sc: any) => {
          const normType = normalizeChargeType(sc.type, sc.amount, sc.rate);
          const rateVal = sc.rate != null && sc.rate !== "" ? sc.rate : sc.amount;
          const amountVal = sc.amount != null && sc.amount !== "" ? sc.amount : sc.rate;
          return {
            chargeName: sc.name,
            name: sc.name,
            type: normType,
            rate: rateVal,
            amount: amountVal,
            utilityService: getSelectedServices(
              sc.utilityServices || sc.utility_services || sc.utilityService,
            ),
            parameter: String(sc.parameterId || sc.parameter_id || sc.parameter || ""),
            value: getSelectedValues(
              sc.parameterValues || sc.parameter_values || sc.value,
            ),
          };
        }),
        taxes: (res.taxes || []).flatMap((taxWrapper: any) => {
          const rules =
            taxWrapper.rules && Array.isArray(taxWrapper.rules)
              ? taxWrapper.rules
              : [taxWrapper];
          return rules.map((rule: any) => {
            const normType = normalizeChargeType(rule.type, rule.amount, rule.rate);
            const rateVal = rule.rate != null && rule.rate !== "" ? rule.rate : rule.amount;
            const amountVal = rule.amount != null && rule.amount !== "" ? rule.amount : rule.rate;
            return {
              taxName: rule.name || rule.taxName,
              type: normType,
              rate: rateVal,
              amount: amountVal,
              utilityService: getSelectedServices(
                rule.utilityServices || rule.utility_services || rule.utilityService,
              ),
              parameter: String(
                rule.parameterId ||
                  rule.parameter_id ||
                  rule.parameter ||
                  taxWrapper.parameterId ||
                  taxWrapper.parameter_id ||
                  taxWrapper.parameter ||
                  "",
              ),
              value: getSelectedValues(
                rule.parameterValues || rule.parameter_values || rule.value,
              ),
            };
          });
        }),
      };

      let hasParamFixed = (res.fixedCharges || res.fixed_charges || []).some(
        (fc: any) => fc.mode === "parameter_wise",
      );
      if (hasParamFixed) setFixedChargeMode("parameter-wise");

      let hasParamSurcharge = (res.surcharges || []).some(
        (sc: any) => sc.mode === "parameter_wise",
      );
      if (hasParamSurcharge) setSurchargeMode("parameter-wise");

      let hasParamTax = (res.taxes || []).some((tw: any) =>
        tw.mode === "parameter_wise" ||
        (tw.rules || []).some((r: any) => r.mode === "parameter_wise"),
      );
      if (hasParamTax) setTaxMode("parameter-wise");

      FormService.populateForm(chargesForm, mappedData, [
        { name: "linkedCharges", type: "form-array" },
        { name: "fixedCharges", type: "form-array" },
        { name: "surcharges", type: "form-array" },
        { name: "taxes", type: "form-array" },
      ] as any[]);

      isPopulated.current = true;
    }
  }, [planDetail, planId, chargesForm, allParametersList]);

  const onDataChangeRef = useRef(onDataChange);
  const dataRef = useRef(data);

  useEffect(() => {
    onDataChangeRef.current = onDataChange;
    dataRef.current = data;
  }, [onDataChange, data]);

  useEffect(() => {
    if (!onDataChangeRef.current) return;

    // Push the current form and mode data whenever mode changes
    onDataChangeRef.current({
      ...dataRef.current,
      ...chargesForm.getValues(),
      taxMode,
      fixedChargeMode,
      surchargeMode,
    });

    // Subscribe to form changes without triggering component re-renders
    const subscription = chargesForm.watch((value) => {
      onDataChangeRef.current?.({
        ...dataRef.current,
        ...value,
        taxMode,
        fixedChargeMode,
        surchargeMode,
      });
    });

    return () => subscription.unsubscribe();
  }, [chargesForm, taxMode, fixedChargeMode, surchargeMode]);

  const handleNextSubmit = async () => {
    const isValid = await chargesForm.trigger();
    if (!isValid) return;

    const formData = chargesForm.getValues();

    // 1. Check duplicate names in Linked Charges
    const dupLinked = findDuplicateName(
      formData.linkedCharges,
      (it) => it?.name,
    );
    if (dupLinked) {
      toast({
        variant: "destructive",
        title: "Duplicate Linked Charge Name",
        description: `Duplicate name "${dupLinked}" found in Linked Charges. Each linked charge must have a unique name.`,
      });
      return;
    }

    // 2. Check duplicate names in Taxes
    const dupTax = findDuplicateName(
      formData.taxes,
      (it) => it?.taxName || it?.name,
    );
    if (dupTax) {
      toast({
        variant: "destructive",
        title: "Duplicate Tax Name",
        description: `Duplicate name "${dupTax}" found in Taxes. Each tax must have a unique name.`,
      });
      return;
    }

    // 3. Check duplicate names in Fixed Charges
    const dupFixed = findDuplicateName(
      formData.fixedCharges,
      (it) => it?.name || it?.chargeName,
    );
    if (dupFixed) {
      toast({
        variant: "destructive",
        title: "Duplicate Fixed Charge Name",
        description: `Duplicate name "${dupFixed}" found in Fixed Charges. Each fixed charge must have a unique name.`,
      });
      return;
    }

    // 4. Check duplicate names in Surcharges
    const dupSurcharge = findDuplicateName(
      formData.surcharges,
      (it) => it?.name || it?.chargeName,
    );
    if (dupSurcharge) {
      toast({
        variant: "destructive",
        title: "Duplicate Surcharge Name",
        description: `Duplicate name "${dupSurcharge}" found in Surcharges. Each surcharge must have a unique name.`,
      });
      return;
    }

    if (isValid) {
      if (planId && planId !== "create") {
        try {
          const payload = {
            current_step: 4,
            remote_utility_id: remoteUtilityId,
            charges_config: {
              linked_charges:
                formData.linkedCharges?.map((item: any, i: number) => ({
                  name: item.name,
                  linked_service: item.linkedService || null,
                  charge_rate: isNaN(Number(item.chargeUnit))
                    ? item.rateAmount || 0
                    : Number(item.chargeUnit),
                  unit: "$/KL",
                  usage: Number(item.usage || 0),
                })) || [],
              fixed_charges:
                formData.fixedCharges?.map((item: any, i: number) => {
                  const utility_services = getSelectedServices(
                    item.utilityService,
                  );
                  const paramObj = allParametersList.find(
                    (p: any) => String(p.id) === String(item.parameter),
                  );
                  const paramCode =
                    paramObj?.code || item.code || item.parameter_code || "";
                  const paramValues = getSelectedValues(
                    item.value || item.parameter_values || item.parameterValues,
                  );
                  return {
                    mode:
                      fixedChargeMode === "parameter-wise"
                        ? "parameter_wise"
                        : "common",
                    name: item.name || item.chargeName,
                    amount: Number(item.amount || 0),
                    include_in: item.includeIn,
                    ...(fixedChargeMode === "parameter-wise"
                      ? {
                          utility_services,
                          parameter_id: Number(item.parameter) || paramObj?.id || null,
                          parameter_code: paramCode,
                          parameter_values: paramValues,
                        }
                      : {}),
                  };
                }) || [],
              surcharges:
                formData.surcharges?.map((item: any, i: number) => {
                  const utility_services = getSelectedServices(
                    item.utilityService,
                  );
                  const paramObj = allParametersList.find(
                    (p: any) => String(p.id) === String(item.parameter),
                  );
                  const paramCode =
                    paramObj?.code || item.code || item.parameter_code || "";
                  const paramValues = getSelectedValues(
                    item.value || item.parameter_values || item.parameterValues,
                  );
                  const surchargeAmount = Number(item.amount || item.rate || 0);
                  const surchargeRate = Number(item.rate || 0);
                  return {
                    mode:
                      surchargeMode === "parameter-wise"
                        ? "parameter_wise"
                        : "common",
                    name: item.name || item.chargeName,
                    type: item.type,
                    rate: surchargeRate,
                    amount: surchargeAmount,
                    ...(surchargeMode === "parameter-wise"
                      ? {
                          utility_services,
                          parameter_id: Number(item.parameter) || paramObj?.id || null,
                          parameter_code: paramCode,
                          parameter_values: paramValues,
                        }
                      : {}),
                  };
                }) || [],
              taxes:
                formData.taxes?.map((item: any, i: number) => {
                  const utility_services = getSelectedServices(
                    item.utilityService,
                  );
                  const paramObj = allParametersList.find(
                    (p: any) =>
                      String(p.id) === String(item.parameter) ||
                      String(p.name).toLowerCase() === String(item.parameter).toLowerCase()
                  );
                  const paramCode =
                    paramObj?.code || item.code || item.parameter_code || "";
                  const taxAmount = Number(item.amount || item.rate || 0);
                  const taxRate = Number(item.rate || 0);
                  const paramValues = getSelectedValues(
                    item.value || item.parameter_values || item.parameterValues,
                  );
                  return {
                    mode:
                      taxMode === "parameter-wise"
                        ? "parameter_wise"
                        : "common",
                    name: item.taxName || item.name,
                    type: item.type,
                    rate: taxRate,
                    amount: taxAmount,
                    ...(taxMode === "parameter-wise"
                      ? {
                          utility_services,
                          parameter_id: Number(item.parameter) || paramObj?.id || null,
                          parameter_code: paramCode,
                          parameter_values: paramValues,
                          amount: taxAmount,
                          priority: 1,
                        }
                      : {}),
                  };
                }) || [],
            },
          };
          if (action !== "edit") {
            await updatePlan({ planId, payload });
          }
          if (onNext) onNext();
        } catch (error) {
          console.error("Failed to update charges config:", error);
        }
      } else {
        if (onNext) onNext();
      }
    }
  };

  const isLinkedChargeDeletable = React.useCallback(
    (item: any) => {
      if (!item) return true;
      const responseData = servicesResult?.result || servicesResult;
      if (!Array.isArray(responseData)) return true;
      const serviceObj = responseData.find(
        (s: any) => String(s.name) === String(item.name),
      );
      if (serviceObj && serviceObj.isMeterRequired === false) {
        return false;
      }
      return true;
    },
    [servicesResult],
  );

  return (
    <div className="space-y-8">
      {/* 1. Linked Charges */}
      <SharedFormArray
        name="linkedCharges"
        form={chargesForm}
        itemFields={linkedChargesFields}
        variant="table"
        label="Linked Charges"
        helperText="Charges whose value is derived from another utility service's consumption — e.g. sewer billed as a % of water usage, or a per-unit fuel surcharge added to electricity."
        emptyMessage="No linked charges added."
        addButtonText="Add"
        footerText="Linked charge = linked service usage × Usage% × Charge $/unit"
        isDeletable={isLinkedChargeDeletable}
      />

      {/* 2. Taxes */}
      <SharedFormArray
        name="taxes"
        form={chargesForm}
        itemFields={
          taxMode === "common" ? commonTaxFields : parameterWiseTaxFields
        }
        variant={taxMode === "common" ? "row" : "card"}
        gridColumns={taxMode === "common" ? undefined : 12}
        label="Taxes"
        helperText="Taxes applied at the bill subtotal. In Common mode, set a flat tax for all consumers. In Parameter-wise mode, vary the tax by customer class, account type, or any attribute."
        emptyMessage="No taxes added yet."
        addButtonText={taxMode === "common" ? "Add Tax" : "Add Rule"}
        cloneable={true}
        headerRightAction={
          <SegmentedControl
            variant="solid"
            value={taxMode}
            onChange={handleTaxModeChange}
            options={[
              { label: "Common", value: "common" },
              { label: "Parameter-wise", value: "parameter-wise" },
            ]}
          />
        }
      />

      {/* 3. Fixed Charges */}
      <SharedFormArray
        name="fixedCharges"
        form={chargesForm}
        cloneable={true}
        itemFields={
          fixedChargeMode === "common"
            ? commonFixedChargeFields
            : parameterWiseFixedChargeFields
        }
        variant={fixedChargeMode === "common" ? "row" : "card"}
        gridColumns={fixedChargeMode === "common" ? undefined : 12}
        label="Fixed Charges"
        helperText="Recurring fixed amounts. Choose when the charge appears on a consumer's bill."
        emptyMessage="No fixed charges added."
        addButtonText="Add"
        headerRightAction={
          <SegmentedControl
            variant="solid"
            value={fixedChargeMode}
            onChange={handleFixedChargeModeChange}
            options={[
              { label: "Common", value: "common" },
              { label: "Parameter-wise", value: "parameter-wise" },
            ]}
          />
        }
      />

      {/* 4. Surcharges */}
      <SharedFormArray
        name="surcharges"
        form={chargesForm}
        itemFields={
          surchargeMode === "common"
            ? commonSurchargeFields
            : parameterWiseSurchargeFields
        }
        variant={surchargeMode === "common" ? "row" : "card"}
        gridColumns={surchargeMode === "common" ? undefined : 12}
        label="Surcharges"
        helperText="Pass-through adjustments calculated as a % of consumption charges."
        emptyMessage="No surcharges added."
        addButtonText="Add"
        cloneable={true}
        headerRightAction={
          <SegmentedControl
            variant="solid"
            value={surchargeMode}
            onChange={handleSurchargeModeChange}
            options={[
              { label: "Common", value: "common" },
              { label: "Parameter-wise", value: "parameter-wise" },
            ]}
          />
        }
      />

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between mt-6 border-t pt-6">
        <div>
          {!isFirstStep && onPrevious && (
            <Button type="button" variant="outline" onClick={onPrevious}>
              Previous
            </Button>
          )}
        </div>
        <Button type="button" onClick={handleNextSubmit} disabled={isPending}>
          {isPending ? "Saving..." : isLastStep ? "Finish" : "Next"}
        </Button>
      </div>
    </div>
  );
};
